import React, { useState, useEffect } from 'react';
import { useNotification } from '../../components/member/NotificationProvider';
import { getMyStore, getStoreMPAccount, disconnectStoreMPAccount } from '../../services/supabaseService';

export const MercadoPagoSetup: React.FC = () => {
    const { showNotification } = useNotification();
    const [store, setStore] = useState<any>(null);
    const [loadingStore, setLoadingStore] = useState(true);
    const [mpAccount, setMpAccount] = useState<any>(null);
    const [loadingMp, setLoadingMp] = useState(false);

    useEffect(() => {
        const fetchStoreAndMP = async () => {
            setLoadingStore(true);
            try {
                const myStore = await getMyStore();
                if (myStore) {
                    setStore(myStore);
                    const mpData = await getStoreMPAccount(myStore.id);
                    setMpAccount(mpData);
                }
            } catch (err) {
                console.error("Error loading store / MP config:", err);
                showNotification("Erro ao carregar dados do lojista", "error");
            } finally {
                setLoadingStore(false);
            }
        };

        fetchStoreAndMP();
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const mpConnected = searchParams.get('mp_connected');
        const mpError = searchParams.get('mp_error');
        
        if (mpConnected === 'true') {
            showNotification("Mercado Pago conectado com sucesso!", "success");
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            if (store) {
                getStoreMPAccount(store.id).then(setMpAccount);
            }
        } else if (mpError) {
            showNotification(decodeURIComponent(mpError), "error");
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [window.location.search, store]);

    const handleConnectMP = () => {
        if (!store?.id) {
            showNotification("ID da loja não encontrado. Recarregue a página.", "error");
            return;
        }
        setLoadingMp(true);
        window.location.href = `/oauth?store_id=${store.id}`;
    };

    const handleDisconnectMP = async () => {
        if (!store?.id) return;
        if (!window.confirm("Deseja realmente desconectar sua conta do Mercado Pago? Seus compradores não poderão mais pagar de forma automatizada no Pix e Cartão de Crédito.")) {
            return;
        }
        setLoadingMp(true);
        try {
            const success = await disconnectStoreMPAccount(store.id);
            if (success) {
                setMpAccount(null);
                showNotification("Conta do Mercado Pago desconectada com sucesso!", "success");
            } else {
                showNotification("Erro ao desconectar conta", "error");
            }
        } catch (err) {
            console.error(err);
            showNotification("Erro ao desconectar conta", "error");
        } finally {
            setLoadingMp(false);
        }
    };

    if (loadingStore) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-500 font-black uppercase text-[10px] tracking-widest animate-pulse">Carregando informações...</p>
                </div>
            </div>
        );
    }

    if (!store) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 text-center space-y-4">
                <div className="w-16 h-16 rounded-[20px] bg-red-500/10 text-red-400 flex items-center justify-center mx-auto border border-red-500/15">
                    <i className="fas fa-store-slash text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-wider">Acesso não autorizado</h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                    Você precisa criar uma loja ou estar autenticado como lojista para acessar as configurações de pagamento.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-8 animate-in fade-in duration-300">
            <header className="space-y-2 border-b border-slate-800/60 pb-6">
                <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-purple-600/10 text-purple-400 border border-purple-500/15 text-[9px] font-black uppercase tracking-widest rounded-full">Painel Lojista</span>
                    <span className="text-slate-600 text-xs">•</span>
                    <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">{store.name}</span>
                </div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tight italic">
                    Conectar Mercado Pago
                </h1>
                <p className="text-sm text-slate-500 max-w-2xl">
                    Integre sua conta do Mercado Pago para receber pagamentos de forma automatizada no Pix, Cartão de Crédito e Boleto com atualização em tempo real de status de pedidos.
                </p>
            </header>

            <div className="bg-slate-900/50 border border-slate-800 rounded-[32px] p-6 md:p-8 space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800/60 pb-6">
                    <div>
                        <h3 className="text-base font-black text-white flex items-center italic uppercase">
                            <span className="w-1.5 h-5 bg-purple-600 rounded-full mr-3"></span>
                            Status da Integração
                        </h3>
                        <p className="text-slate-500 text-xs mt-1">
                            Verifique se a sua conta já está vinculada ou inicie o fluxo OAuth de autorização segura.
                        </p>
                    </div>
                </div>

                {mpAccount ? (
                    <div className="p-8 rounded-[24px] bg-emerald-500/5 border border-emerald-500/20 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                                    <i className="fas fa-check-circle text-2xl animate-pulse"></i>
                                </div>
                                <div>
                                    <h4 className="text-sm font-black text-white uppercase tracking-wider">Mercado Pago Conectado</h4>
                                    <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest mt-0.5">Sua loja está totalmente pronta para receber pagamentos automatizados</p>
                                </div>
                            </div>
                            <div className="pl-13 space-y-1 text-slate-400 text-xs font-medium">
                                <p>
                                    <span className="text-slate-500 font-bold uppercase text-[10px] tracking-widest block md:inline md:mr-2">ID do Usuário MP:</span> 
                                    <span className="font-mono text-xs text-slate-300">{mpAccount.mp_user_id}</span>
                                </p>
                                <p>
                                    <span className="text-slate-500 font-bold uppercase text-[10px] tracking-widest block md:inline md:mr-2">Conectado em:</span> 
                                    <span className="text-xs text-slate-300">
                                        {new Date(mpAccount.created_at).toLocaleDateString("pt-BR")} às {new Date(mpAccount.created_at).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleDisconnectMP}
                                disabled={loadingMp}
                                className="bg-red-500/10 hover:bg-red-500 hover:text-white text-red-400 font-black px-6 py-3.5 rounded-2xl border border-red-500/20 active:scale-95 text-[10px] uppercase tracking-widest transition-all"
                            >
                                {loadingMp ? 'DESCONECTANDO...' : 'DESCONECTAR CONTA'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-8 rounded-[32px] bg-slate-950/40 border border-dashed border-slate-800 text-center flex flex-col items-center justify-center space-y-6">
                        <div className="w-16 h-16 rounded-[20px] bg-purple-600/10 text-purple-400 flex items-center justify-center border border-purple-500/10">
                            <i className="fas fa-handshake text-3xl"></i>
                        </div>
                        <div className="max-w-md space-y-2">
                            <h4 className="text-base font-black text-white uppercase tracking-wider">Vincule seu Mercado Pago</h4>
                            <p className="text-xs text-slate-400 leading-relaxed">
                                Ao vincular a conta do Mercado Pago, seus compradores poderão realizar pagamentos diretamente na finalização das compras. Os pedidos serão marcados como pagos e atualizados instantaneamente!
                            </p>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={handleConnectMP}
                                disabled={loadingMp}
                                className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-black px-8 py-4 rounded-xl shadow-xl shadow-purple-600/20 active:scale-95 text-[10px] uppercase tracking-widest transition-all flex items-center gap-2.5"
                            >
                                <i className="fas fa-link"></i> {loadingMp ? 'REDIRECIONANDO...' : 'CONECTAR CONTA DO MERCADO PAGO'}
                            </button>
                        </div>
                    </div>
                )}
                
                <div className="rounded-[24px] bg-slate-900/30 p-6 border border-slate-800/80 space-y-4">
                    <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center">
                        <i className="fas fa-shield-halved text-purple-500 mr-2 text-sm"></i>
                        Segurança e Criptografia
                    </h4>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                        Todas as credenciais de autenticação (incluindo o token de acesso e de atualização gerados pelo Mercado Pago) são criptografadas localmente na camada do servidor usando o algoritmo de criptografia simétrica <strong className="text-white">AES-256 (AES-256-CBC)</strong> com chave privada segura. Nenhuma credencial confidencial é exposta em logs, no front-end ou para outros usuários.
                    </p>
                </div>
            </div>
        </div>
    );
};
